import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Coffee, Filter, RefreshCw, Search, X } from "lucide-react";
import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";

const LIMIT_MINUTES = 45;

const todayKey = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
};

const formatDate = (value) => {
    if (!value) return "-";
    return new Date(`${value}T12:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const LunchAlerts = () => {
    const { employee } = useAuth();
    const canViewPage = employee?.role === "lead" || employee?.role === "hr";
    const [alerts, setAlerts] = useState([]);
    const [scope, setScope] = useState("today");
    const [search, setSearch] = useState("");
    const [teamFilter, setTeamFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState(() => todayKey());
    const [minMinutes, setMinMinutes] = useState(LIMIT_MINUTES);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const teamOptions = useMemo(() => (
        [...new Set(alerts.map((alert) => alert.team || alert.employee?.team).filter(Boolean))]
            .sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: "base" }))
    ), [alerts]);

    const filteredAlerts = useMemo(() => {
        const query = search.trim().toLowerCase();
        const minimum = Math.max(LIMIT_MINUTES, Number(minMinutes || LIMIT_MINUTES));

        return alerts.filter((alert) => {
            const minutes = Number(alert.minutes || 0);
            const employeeName = String(alert.employee?.name || "").toLowerCase();
            const employeeEmail = String(alert.employee?.email || "").toLowerCase();
            const employeeId = String(alert.employee?.employeeId || "").toLowerCase();
            const team = String(alert.team || alert.employee?.team || "");

            const matchesExceeded = minutes > LIMIT_MINUTES && minutes >= minimum;
            const matchesSearch = !query
                || employeeName.includes(query)
                || employeeEmail.includes(query)
                || employeeId.includes(query);
            const matchesTeam = teamFilter === "all" || team === teamFilter;
            const matchesDate = !dateFilter || alert.date === dateFilter;

            return matchesExceeded && matchesSearch && matchesTeam && matchesDate;
        });
    }, [alerts, dateFilter, minMinutes, search, teamFilter]);

    const stats = useMemo(() => ({
        today: filteredAlerts.filter((alert) => alert.date === todayKey()).length,
        maxMinutes: filteredAlerts.reduce((max, alert) => Math.max(max, Math.round(Number(alert.minutes || 0))), 0),
    }), [filteredAlerts]);

    const resetFilters = () => {
        setSearch("");
        setTeamFilter("all");
        setDateFilter(todayKey());
        setMinMinutes(LIMIT_MINUTES);
        setScope("today");
    };

    const fetchAlerts = useCallback(async () => {
        if (!canViewPage) return;

        setLoading(true);
        setError("");
        const scopedDate = scope === "today" ? todayKey() : dateFilter;
        if (scope === "today" && dateFilter !== scopedDate) setDateFilter(scopedDate);

        try {
            const res = await api.get("/api/employee/lunch-alerts", {
                cache: false,
                params: { status: "all", date: scopedDate || undefined, _t: Date.now() },
            });
            setAlerts(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            const statusCode = err.response?.status;
            const message = err.response?.data?.message || err.response?.data || err.message || "Failed to load lunch alerts";
            setError(statusCode ? `Failed to load lunch alerts (${statusCode}): ${message}` : `Failed to load lunch alerts: ${message}`);
        } finally {
            setLoading(false);
        }
    }, [canViewPage, dateFilter, scope]);

    useEffect(() => {
        fetchAlerts();
        const id = window.setInterval(fetchAlerts, 10000);
        return () => window.clearInterval(id);
    }, [fetchAlerts]);

    if (!canViewPage) {
        return (
            <main className="min-h-[calc(100vh-72px)] bg-[var(--bg-primary)] p-4 text-[var(--text-primary)] sm:p-6">
                <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5 shadow-[var(--shadow-soft)]">
                    <h1 className="text-xl font-black">Lunch Break Alerts</h1>
                    <p className="mt-1 text-sm text-[var(--text-secondary)]">Lunch alerts are shown through your notifications.</p>
                </section>
            </main>
        );
    }

    return (
        <main className="min-h-[calc(100vh-72px)] bg-[var(--bg-primary)] p-4 text-[var(--text-primary)] sm:p-6">
            <section className="mx-auto max-w-7xl space-y-5">
                <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5 shadow-[var(--shadow-soft)]">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500 text-white">
                                <Coffee size={22} />
                            </div>
                            <div>
                                <p className="text-xs font-black uppercase tracking-wide text-amber-500">Lunch Monitoring</p>
                                <h1 className="text-2xl font-black sm:text-3xl">Lunch Break Alerts</h1>
                                <p className="mt-1 text-sm text-[var(--text-secondary)]">
                                    {employee?.role === "hr" ? "All employee lunch alerts." : "Your team lunch alerts."} Allowed lunch duration is {LIMIT_MINUTES} minutes.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            {[
                                { value: "today", label: "Today Lunch" },
                                { value: "all", label: "All" },
                            ].map((item) => (
                                <button
                                    key={item.value}
                                    type="button"
                                    onClick={() => {
                                        setScope(item.value);
                                        if (item.value === "today") setDateFilter(todayKey());
                                    }}
                                    className={`h-10 rounded-xl border px-4 text-sm font-bold transition-all ${scope === item.value ? "border-amber-500 bg-amber-500 text-white" : "border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)]"}`}
                                >
                                    {item.label}
                                </button>
                            ))}

                        </div>
                    </div>
                </section>

                <section className="grid gap-3 sm:grid-cols-3">
                    <Metric label="Total exceeded" value={filteredAlerts.length} tone="text-red-500" />
                    <Metric label="Today exceeded" value={stats.today} tone="text-emerald-500" />
                    <Metric label="Highest lunch" value={`${stats.maxMinutes || 0}m`} tone="text-amber-500" />
                </section>

                <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)]">
                    <div className="mb-3 flex items-center gap-2 text-sm font-black text-[var(--text-primary)]">
                        <Filter size={16} className="text-amber-500" />
                        Filter exceeded lunch records
                    </div>
                    <div className="grid gap-3 md:grid-cols-[minmax(220px,1fr)_180px_170px_150px_auto]">
                        <label className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                            <input
                                type="search"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search employee, email, ID"
                                className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] pl-9 pr-3 text-sm font-semibold outline-none focus:border-amber-500"
                            />
                        </label>
                        <select
                            value={teamFilter}
                            onChange={(event) => setTeamFilter(event.target.value)}
                            className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-semibold outline-none focus:border-amber-500"
                        >
                            <option value="all">All teams</option>
                            {teamOptions.map((team) => (
                                <option key={team} value={team}>{team}</option>
                            ))}
                        </select>
                        <input
                            type="date"
                            value={dateFilter}
                            onChange={(event) => {
                                setDateFilter(event.target.value);
                                setScope("all");
                            }}
                            className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-sm font-semibold outline-none focus:border-amber-500"
                        />
                        <label className="flex h-11 items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3">
                            <span className="shrink-0 text-xs font-black text-[var(--text-secondary)]">Min</span>
                            <input
                                type="number"
                                min={LIMIT_MINUTES}
                                value={minMinutes}
                                onChange={(event) => setMinMinutes(Math.max(LIMIT_MINUTES, Number(event.target.value || LIMIT_MINUTES)))}
                                className="min-w-0 flex-1 bg-transparent text-sm font-black outline-none"
                            />
                            <span className="text-xs font-black text-[var(--text-secondary)]">m</span>
                        </label>
                        <button
                            type="button"
                            onClick={resetFilters}
                            className="flex h-11 items-center justify-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 text-sm font-black text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]"
                        >
                            <X size={16} />
                            Clear
                        </button>
                    </div>
                    <p className="mt-3 text-xs font-semibold text-[var(--text-secondary)]">
                        Showing {filteredAlerts.length} employee lunch record{filteredAlerts.length === 1 ? "" : "s"} over {LIMIT_MINUTES} minutes
                        {dateFilter ? ` for ${formatDate(dateFilter)}.` : "."}
                    </p>
                </section>

                {error && (
                    <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-500">
                        {error}
                    </div>
                )}

                <section className="overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-[var(--shadow-soft)]">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[780px] text-left text-sm">
                            <thead className="border-b border-[var(--border-color)] bg-[var(--bg-secondary)] text-xs uppercase text-[var(--text-secondary)]">
                                <tr>
                                    <th className="px-4 py-3">Employee</th>
                                    <th className="px-4 py-3">Team</th>
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Lunch Start</th>
                                    <th className="px-4 py-3">Lunch End</th>
                                    <th className="px-4 py-3">Duration</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!filteredAlerts.length && (
                                    <tr>
                                        <td colSpan="6" className="px-4 py-10 text-center text-[var(--text-secondary)]">
                                            {loading ? "Loading lunch alerts..." : "No employees exceeded the selected lunch filters."}
                                        </td>
                                    </tr>
                                )}
                                {filteredAlerts.map((alert) => (
                                    <tr key={alert._id} className="border-b border-[var(--border-color)] last:border-0">
                                        <td className="px-4 py-3">
                                            <div className="font-black">{alert.employee?.name || "Employee"}</div>
                                            <div className="text-xs text-[var(--text-secondary)]">{alert.employee?.employeeId || alert.employee?.email || "-"}</div>
                                        </td>
                                        <td className="px-4 py-3">{alert.team || alert.employee?.team || "-"}</td>
                                        <td className="px-4 py-3">{formatDate(alert.date)}</td>
                                        <td className="px-4 py-3">{formatTime(alert.lunchStart)}</td>
                                        <td className="px-4 py-3">{formatTime(alert.lunchEnd)}</td>
                                        <td className="px-4 py-3">
                                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-black text-amber-600">
                                                <AlertTriangle size={13} />
                                                {Math.round(alert.minutes || 0)}m
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            </section>
        </main>
    );
};

const Metric = ({ label, value, tone }) => (
    <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)]">
        <p className="text-xs font-black uppercase text-[var(--text-secondary)]">{label}</p>
        <p className={`mt-1 text-2xl font-black ${tone}`}>{value}</p>
    </div>
);

export default LunchAlerts;
